/**
 * Inline citations.
 *
 * Each [n] in the article is already a working link to #ref-n, server-side.
 * This adds two things on top, both purely enhancement:
 *
 *   1. A hover/focus card showing the source without leaving your place.
 *   2. Back-links on each reference to the citations that point at it.
 *
 * The card is cloned from the reference's own markup rather than duplicated
 * into a data attribute, so a source is written once and cannot drift.
 *
 * WCAG 1.4.13 (content on hover or focus) is the reason for the delays and
 * the Escape handler: the card must be dismissible, hoverable and persistent.
 */

(function (Drupal, once) {
  "use strict";

  const SHOW_DELAY = 120;
  const HIDE_DELAY = 220;
  const GAP = 8;

  /** @type {HTMLElement|null} */
  let card = null;
  /** @type {HTMLElement|null} */
  let owner = null;
  let showTimer = null;
  let hideTimer = null;

  function ensureCard() {
    if (card) {
      return card;
    }
    card = document.createElement("div");
    card.className = "k-cite-card";
    card.id = "k-cite-card";
    card.setAttribute("role", "tooltip");
    card.hidden = true;
    // Hovering the card itself must not dismiss it.
    card.addEventListener("mouseenter", cancelHide);
    card.addEventListener("mouseleave", scheduleHide);
    document.body.appendChild(card);
    return card;
  }

  /**
   * Positions the card under its citation, nudged inside the viewport.
   *
   * @param {HTMLElement} anchor
   */
  function position(anchor) {
    const rect = anchor.getBoundingClientRect();
    const cardRect = card.getBoundingClientRect();
    const scrollX = window.scrollX;
    const scrollY = window.scrollY;

    let left = rect.left + scrollX + rect.width / 2 - cardRect.width / 2;
    const min = scrollX + GAP;
    const max = scrollX + document.documentElement.clientWidth - cardRect.width - GAP;
    left = Math.max(min, Math.min(left, max));

    // Flip above when there is not room below.
    const below = rect.bottom + scrollY + GAP;
    const roomBelow = window.innerHeight - rect.bottom;
    const top =
      roomBelow < cardRect.height + GAP && rect.top > cardRect.height + GAP
        ? rect.top + scrollY - cardRect.height - GAP
        : below;

    card.style.left = `${Math.round(left)}px`;
    card.style.top = `${Math.round(top)}px`;
  }

  /**
   * @param {HTMLElement} anchor
   */
  function show(anchor) {
    const number = anchor.dataset.kCite;
    const reference = document.getElementById(`ref-${number}`);
    if (!reference) {
      return;
    }

    const body = reference.querySelector(".k-reference__body");
    if (!body) {
      return;
    }

    ensureCard();
    card.innerHTML = "";

    const label = document.createElement("span");
    label.className = "k-cite-card__number";
    label.textContent = Drupal.t("Reference @n", { "@n": number });
    card.appendChild(label);

    const clone = body.cloneNode(true);
    // The clone must not duplicate ids or become a second tab stop.
    clone.removeAttribute("id");
    clone.querySelectorAll("[id]").forEach((el) => el.removeAttribute("id"));
    clone.querySelectorAll("a").forEach((el) => el.setAttribute("tabindex", "-1"));
    // Back-links point from the reference list into the article. Inside the
    // card they would offer to send the reader back to where they already
    // are, so they are dropped from the preview.
    clone.querySelectorAll(".k-reference__backlinks").forEach((el) => el.remove());
    card.appendChild(clone);

    card.hidden = false;
    position(anchor);
    card.dataset.kCiteCardVisible = "true";

    anchor.setAttribute("aria-describedby", card.id);
    reference.dataset.kReferenceActive = "true";
    owner = anchor;
  }

  function hide() {
    if (!card || !owner) {
      return;
    }
    const reference = document.getElementById(`ref-${owner.dataset.kCite}`);
    if (reference) {
      delete reference.dataset.kReferenceActive;
    }
    owner.removeAttribute("aria-describedby");
    delete card.dataset.kCiteCardVisible;
    card.hidden = true;
    owner = null;
  }

  function cancelHide() {
    window.clearTimeout(hideTimer);
  }

  function scheduleHide() {
    cancelHide();
    hideTimer = window.setTimeout(hide, HIDE_DELAY);
  }

  /**
   * @param {HTMLElement} anchor
   */
  function scheduleShow(anchor) {
    cancelHide();
    window.clearTimeout(showTimer);
    if (owner === anchor) {
      return;
    }
    showTimer = window.setTimeout(() => {
      hide();
      show(anchor);
    }, SHOW_DELAY);
  }

  /**
   * Adds a back-link on each reference for every citation pointing at it.
   *
   * Done here rather than server-side because only the rendered page knows
   * which references were actually cited - writing them in the template
   * would mean dead links on any source that is listed but never referenced.
   *
   * @param {HTMLElement} root
   */
  function addBackLinks(root) {
    const byNumber = new Map();

    root.querySelectorAll("[data-k-cite]").forEach((anchor) => {
      if (!anchor.id) {
        return;
      }
      const list = byNumber.get(anchor.dataset.kCite) || [];
      list.push(anchor.id);
      byNumber.set(anchor.dataset.kCite, list);
    });

    byNumber.forEach((ids, number) => {
      const reference = document.getElementById(`ref-${number}`);
      const body = reference && reference.querySelector(".k-reference__body");
      if (!body || body.querySelector(".k-reference__backlinks")) {
        return;
      }

      const wrap = document.createElement("span");
      wrap.className = "k-reference__backlinks";

      ids.forEach((id, i) => {
        const link = document.createElement("a");
        link.className = "k-reference__backlink";
        link.href = `#${id}`;
        link.textContent = ids.length > 1 ? `↩${i + 1}` : "↩";
        link.setAttribute(
          "aria-label",
          ids.length > 1
            ? Drupal.t("Back to citation @i of reference @n", { "@i": i + 1, "@n": number })
            : Drupal.t("Back to citation of reference @n", { "@n": number }),
        );
        wrap.appendChild(link);
      });

      body.appendChild(wrap);
    });
  }

  Drupal.behaviors.keidoArticleCitations = {
    attach(context) {
      once("keidoCitation", "[data-k-cite]", context).forEach((anchor) => {
        anchor.addEventListener("mouseenter", () => scheduleShow(anchor));
        anchor.addEventListener("mouseleave", () => {
          window.clearTimeout(showTimer);
          scheduleHide();
        });
        anchor.addEventListener("focus", () => {
          window.clearTimeout(showTimer);
          hide();
          show(anchor);
        });
        anchor.addEventListener("blur", hide);
      });

      once("keidoCitationRoot", "[data-k-article-main]", context).forEach(addBackLinks);

      once("keidoCitationGlobal", "body").forEach((body) => {
        // Dismissible without moving the pointer or the focus.
        body.addEventListener("keydown", (event) => {
          if (event.key === "Escape" && owner) {
            window.clearTimeout(showTimer);
            hide();
          }
        });
        window.addEventListener(
          "scroll",
          () => {
            if (owner) {
              position(owner);
            }
          },
          { passive: true },
        );
      });
    },
  };
})(Drupal, once);
